import { createBrowserRouter } from 'react-router-dom'
import { RootLayout } from '@/components/layout/root-layout'
import { AuthLayout } from '@/components/layout/auth-layout'
import { DashboardLayout } from '@/components/layout/dashboard-layout'

// Public pages
import HomePage from '@/pages/HomePage'

// Auth pages
import { LoginPage } from '@/pages/auth/login'
import { RegisterPage } from '@/pages/auth/register'
import { ForgotPasswordPage } from '@/pages/auth/forgot-password'
import { ResetPasswordPage } from '@/pages/auth/reset-password'
import CheckEmailPage from '@/pages/auth/check-email'
import VerifyEmailPage from '@/pages/auth/verify-email'
import SetupPasswordPage from '@/pages/auth/setup-password'
import ChangePasswordPage from '@/pages/auth/change-password'
import InvitePage from '@/pages/auth/invite'
import AuthErrorPage from '@/pages/auth/error'

// Dashboard pages
import { DashboardPage } from '@/pages/dashboard/dashboard'
import { FeedbackPage } from '@/pages/dashboard/feedback'
import { OneOnOnesPage } from '@/pages/dashboard/one-on-ones'
import { CreateOneOnOnePage } from '@/pages/dashboard/one-on-ones/create'
import { OneOnOneHistoryPage } from '@/pages/dashboard/one-on-ones/history'
import { OneOnOneTemplatesPage } from '@/pages/dashboard/one-on-ones/templates'
import { MyTeamPage } from '@/pages/dashboard/my-team'
import { TeamPage } from '@/pages/dashboard/team'
import { TeamDetailPage } from '@/pages/dashboard/team-detail'
import { TeamCreatePage } from '@/pages/dashboard/team-create'
import { MembersPage } from '@/pages/dashboard/members'
import { TemplatesPage } from '@/pages/dashboard/templates'
import { OrganizationPage } from '@/pages/dashboard/organization'
import { CreateOrganizationPage } from '@/pages/dashboard/organization/create'
import { AuditTrailPage } from '@/pages/dashboard/audit-trail'
import { EmailManagementPage } from '@/pages/dashboard/email-management'
import { ProfilePage } from '@/pages/dashboard/profile'
import OneOnOneSetupPageStub from '@/pages/dashboard/one-on-one-setup'
import OneOnOneDetailPlaceholder from '@/pages/dashboard/one-on-ones/[id]'
import OneOnOneEditPlaceholder from '@/pages/dashboard/one-on-ones/[id]/edit'
import OneOnOneSchedulePlaceholder from '@/pages/dashboard/one-on-ones/schedule/[userId]'
import OneOnOneTemplateCreatePlaceholder from '@/pages/dashboard/one-on-ones/templates/create'
import TeamAnalyticsPlaceholder from '@/pages/dashboard/team-analytics'
import TeamHierarchyPlaceholder from '@/pages/dashboard/team-hierarchy'
import TeamMembersPlaceholder from '@/pages/dashboard/team-members'
import TeamMemberProfilePlaceholder from '@/pages/dashboard/team-profile/[id]'
import TeamFeedbackFilterPlaceholder from '@/pages/dashboard/team-feedback-filter'
import TeamSettingsPlaceholder from '@/pages/dashboard/team-settings'
import BillingSuccessPage from '@/pages/dashboard/billing-success'
import BillingCancelPage from '@/pages/dashboard/billing-cancel'
import { FeedbackAnalyticsPage } from '@/pages/dashboard/feedback/analytics'
import { FeedbackHistoryPage } from '@/pages/dashboard/feedback/history'
import { FeedbackContributorsPage } from '@/pages/dashboard/feedback/contributors'
import { FeedbackPendingPage } from '@/pages/dashboard/feedback/pending'
import { TotalFeedbackPage } from '@/pages/dashboard/feedback/total'
import { GivenFeedbackPage } from '@/pages/dashboard/feedback/total/given'
import { ReceivedFeedbackPage } from '@/pages/dashboard/feedback/total/received'

// Error pages
import { NotFoundPage } from '@/pages/404'
import { ErrorPage } from '@/pages/error'
import UnauthorizedPage from '@/pages/401'
import ForbiddenPage from '@/pages/403'
import FeedbackTemplatesRedirect from '@/pages/feedback-templates'

// Public token pages
import PublicFeedbackPage from '@/pages/feedback/public-feedback'
import FeedbackCyclePage from '@/pages/feedback/cycle-summary'
import PublicConductPage from '@/pages/conduct/public-conduct'

// Onboarding
import OrganizationSetupPage from '@/pages/onboarding'
import TeamInvitationPage from '@/pages/onboarding/team'
import OnboardingCompletePage from '@/pages/onboarding/complete'

// Misc public pages
import PrivacyPolicyPage from '@/pages/privacy'
import TermsOfServicePage from '@/pages/terms'
import ReactivatePage from '@/pages/reactivate'

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      // Auth pages directly under root to match Next.js structure
      {
        path: 'login',
        element: <AuthLayout />,
        children: [
          {
            index: true,
            element: <LoginPage />,
          },
        ],
      },
      {
        path: 'signup',
        element: <AuthLayout />,
        children: [
          {
            index: true,
            element: <RegisterPage />,
          },
        ],
      },
      {
        path: 'forgot-password',
        element: <AuthLayout />,
        children: [
          {
            index: true,
            element: <ForgotPasswordPage />,
          },
        ],
      },
      {
        path: 'reset-password',
        element: <AuthLayout />,
        children: [
          {
            index: true,
            element: <ResetPasswordPage />,
          },
        ],
      },
      {
        path: 'check-email',
        element: <AuthLayout />,
        children: [
          {
            index: true,
            element: <CheckEmailPage />,
          },
        ],
      },
      {
        path: 'verify-email',
        element: <AuthLayout />,
        children: [
          {
            index: true,
            element: <VerifyEmailPage />,
          },
        ],
      },
      {
        path: 'setup-password',
        element: <AuthLayout />,
        children: [
          {
            index: true,
            element: <SetupPasswordPage />,
          },
        ],
      },
      {
        path: 'change-password',
        element: <AuthLayout />,
        children: [
          {
            index: true,
            element: <ChangePasswordPage />,
          },
        ],
      },
      {
        path: 'invite',
        element: <AuthLayout />,
        children: [
          {
            index: true,
            element: <InvitePage />,
          },
        ],
      },
      {
        path: 'auth/error',
        element: <AuthErrorPage />,
      },
      {
        path: 'privacy',
        element: <PrivacyPolicyPage />,
      },
      {
        path: 'terms',
        element: <TermsOfServicePage />,
      },
      {
        path: 'reactivate',
        element: <ReactivatePage />,
      },
      {
        path: 'feedback-templates',
        element: <FeedbackTemplatesRedirect />,
      },
      {
        path: 'feedback/:token',
        element: <PublicFeedbackPage />,
      },
      {
        path: 'feedback/cycle/:id',
        element: <FeedbackCyclePage />,
      },
      {
        path: 'conduct/:token',
        element: <PublicConductPage />,
      },
      {
        path: 'onboarding',
        children: [
          {
            index: true,
            element: <OrganizationSetupPage />,
          },
          {
            path: 'team',
            element: <TeamInvitationPage />,
          },
          {
            path: 'complete',
            element: <OnboardingCompletePage />,
          },
        ],
      },
      {
        path: '401',
        element: <UnauthorizedPage />,
      },
      {
        path: '403',
        element: <ForbiddenPage />,
      },
      {
        path: 'dashboard',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <DashboardPage />,
          },
        ],
      },
      // Dashboard routes without prefix (to match Next.js structure)
      {
        path: 'feedback',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <FeedbackPage />,
          },
          {
            path: 'analytics',
            element: <FeedbackAnalyticsPage />,
          },
          {
            path: 'history',
            element: <FeedbackHistoryPage />,
          },
          {
            path: 'contributors',
            element: <FeedbackContributorsPage />,
          },
          {
            path: 'pending',
            element: <FeedbackPendingPage />,
          },
          {
            path: 'total',
            element: <TotalFeedbackPage />,
          },
          {
            path: 'total/given',
            element: <GivenFeedbackPage />,
          },
          {
            path: 'total/received',
            element: <ReceivedFeedbackPage />,
          },
          {
            path: 'total/:filter',
            element: <TotalFeedbackPage />,
          },
        ],
      },
      {
        path: 'one-on-ones',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <OneOnOnesPage />,
          },
          {
            path: 'create',
            element: <CreateOneOnOnePage />,
          },
          {
            path: 'history',
            element: <OneOnOneHistoryPage />,
          },
          {
            path: 'templates',
            element: <OneOnOneTemplatesPage />,
          },
          {
            path: ':id',
            element: <OneOnOneDetailPlaceholder />,
          },
          {
            path: ':id/edit',
            element: <OneOnOneEditPlaceholder />,
          },
          {
            path: 'schedule/:userId',
            element: <OneOnOneSchedulePlaceholder />,
          },
          {
            path: 'templates/create',
            element: <OneOnOneTemplateCreatePlaceholder />,
          },
        ],
      },
      {
        path: 'one-on-one-setup',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <OneOnOneSetupPageStub />,
          },
        ],
      },
      {
        path: 'my-team',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <MyTeamPage />,
          },
        ],
      },
      {
        path: 'team',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <TeamPage />,
          },
          {
            path: 'create',
            element: <TeamCreatePage />,
          },
          {
            path: 'analytics',
            element: <TeamAnalyticsPlaceholder />,
          },
          {
            path: 'hierarchy',
            element: <TeamHierarchyPlaceholder />,
          },
          {
            path: 'members',
            element: <TeamMembersPlaceholder />,
          },
          {
            path: 'profile/:id',
            element: <TeamMemberProfilePlaceholder />,
          },
          {
            path: ':id/feedback-:filter',
            element: <TeamFeedbackFilterPlaceholder />,
          },
          {
            path: ':id/settings',
            element: <TeamSettingsPlaceholder />,
          },
          {
            path: ':id',
            element: <TeamDetailPage />,
          },
        ],
      },
      {
        path: 'members',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <MembersPage />,
          },
        ],
      },
      {
        path: 'templates',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <TemplatesPage />,
          },
        ],
      },
      {
        path: 'organization',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <OrganizationPage />,
          },
          {
            path: 'create',
            element: <CreateOrganizationPage />,
          },
          {
            path: 'billing/success',
            element: <BillingSuccessPage />,
          },
          {
            path: 'billing/cancel',
            element: <BillingCancelPage />,
          },
        ],
      },
      {
        path: 'audit-trail',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <AuditTrailPage />,
          },
        ],
      },
      {
        path: 'email-management',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <EmailManagementPage />,
          },
        ],
      },
      {
        path: 'billing/success',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <BillingSuccessPage />,
          },
        ],
      },
      {
        path: 'billing/cancel',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <BillingCancelPage />,
          },
        ],
      },
      {
        path: 'profile',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <ProfilePage />,
          },
        ],
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
])
